import { Container, FederatedPointerEvent } from "pixi.js";
import { Card } from "./Card";
import { Pile } from "./Pile";
import { GameBoard } from "./GameBoard";
import { GameRules } from "./GameRules";

export interface DragState {
  cards: Card[];
  sourcePile: Pile;
  originalPositions: { x: number; y: number }[];
}

export class DragDropManager {
  private stage: Container;
  private board: GameBoard;
  private rules: GameRules;
  private dragState: DragState | null = null;
  private dragContainer: Container;
  private onMoveComplete?: () => void;
  private onStockClick?: () => void;
  private lastClickCard: Card | null = null;
  private lastClickTime: number = 0;
  private lastClickPosition: { x: number; y: number } | null = null;
  private readonly DOUBLE_CLICK_DELAY = 300; // milliseconds
  private readonly DOUBLE_CLICK_DISTANCE = 10; // pixels - max movement to count as click

  constructor(stage: Container, board: GameBoard, rules: GameRules) {
    this.stage = stage;
    this.board = board;
    this.rules = rules;
    this.dragContainer = new Container();
    this.dragContainer.name = "DragContainer";
    this.stage.addChild(this.dragContainer);

    this.setupStageEvents();
  }

  setOnMoveComplete(callback: () => void): void {
    this.onMoveComplete = callback;
  }

  setOnStockClick(callback: () => void): void {
    this.onStockClick = callback;
  }

  clearDragContainer(): void {
    // Return any cards stuck in drag container to their piles
    while (this.dragContainer.children.length > 0) {
      const child = this.dragContainer.children[0];
      if (child instanceof Card) {
        const pile = this.board.findPileForCard(child);
        if (pile) {
          this.dragContainer.removeChild(child);
          pile.addCard(child);
        } else {
          // Card has no pile, just remove it
          this.dragContainer.removeChild(child);
        }
      } else {
        this.dragContainer.removeChild(child);
      }
    }
    this.dragState = null;
  }

  private setupStageEvents(): void {
    this.stage.eventMode = "static";
    this.stage.on("pointermove", this.handlePointerMove.bind(this));
    this.stage.on("pointerup", (e: FederatedPointerEvent) => this.handlePointerUp(e));
    this.stage.on("pointerupoutside", (e: FederatedPointerEvent) => this.handlePointerUp(e));
  }

  setupCardInteraction(card: Card): void {
    const pile = this.board.findPileForCard(card);
    
    // Disable interaction for cards in completed foundation piles
    if (pile && this.rules.isFoundationComplete(pile)) {
      card.eventMode = "static";
      card.cursor = "default";
      card.removeAllListeners("pointerdown");
      return;
    }
    
    card.eventMode = "static";
    card.cursor = "pointer";
    card.removeAllListeners("pointerdown");
    card.on("pointerdown", (e: FederatedPointerEvent) => this.handleCardPointerDown(e, card));
  }

  private handleCardPointerDown(e: FederatedPointerEvent, card: Card): void {
    e.stopPropagation();
    
    const pile = this.board.findPileForCard(card);
    if (!pile) return;

    // Store click info for double-click detection
    // Convert to stage-local coordinates for consistency
    const stagePos = e.getLocalPosition(this.stage);
    this.lastClickCard = card;
    this.lastClickTime = Date.now();
    this.lastClickPosition = { x: stagePos.x, y: stagePos.y };

    // Handle single click immediately (for dragging and other actions)
    if (pile.type === "stock") {
      this.handleStockCardClick(card, pile);
    } else if (pile.type === "tableau") {
      this.handleTableauCardClick(card, pile);
    } else if (pile.type === "waste" || pile.type === "foundation") {
      this.handleTopCardClick(card, pile);
    }
  }

  private handleTableauCardClick(card: Card, pile: Pile): void {
    const cardIndex = pile.cards.indexOf(card);
    const isTopCard = cardIndex === pile.cards.length - 1;

    if (!card.faceUp && isTopCard) {
      card.faceUp = true;
      this.onMoveComplete?.();
      return;
    }

    if (card.faceUp) {
      this.startDrag(card, pile, cardIndex);
    }
  }

  private handleCardDoubleClick(e: FederatedPointerEvent, card: Card): void {
    e.stopPropagation();
    
    // Clear double-click detection to prevent further processing
    this.lastClickCard = null;
    this.lastClickTime = 0;
    
    const pile = this.board.findPileForCard(card);
    if (!pile) return;

    // Only handle double-click on top cards
    const isTopCard = pile.cards.indexOf(card) === pile.cards.length - 1;
    if (!isTopCard) return;

    // Don't allow double-click on stock
    if (pile.type === "stock") return;

    // Don't allow double-click from completed foundation piles
    if (this.rules.isFoundationComplete(pile)) return;

    // Try to move to foundation - find the appropriate foundation pile
    const foundationPile = this.board.getFoundationPileBySuit(card.suit);
    if (foundationPile && this.rules.canMove([card], pile, foundationPile)) {
      this.moveCardToFoundation(card, pile, foundationPile);
    }
  }

  private moveCardToFoundation(card: Card, sourcePile: Pile, foundationPile: Pile): void {
    // Remove from source
    sourcePile.removeCard(card);
    
    // Add to foundation
    foundationPile.addCard(card);
    this.setupCardInteraction(card);
    
    // Reveal top card of source if it's a tableau
    if (sourcePile.type === "tableau") {
      sourcePile.revealTopCard();
    }
    
    this.onMoveComplete?.();
    
    // Check for completed sequences and auto-move to foundation
    this.checkAndAutoMoveCompletedSequences();
  }

  private handleStockCardClick(card: Card, pile: Pile): void {
    // Stock cards should trigger stock click handler when clicked
    const isTopCard = pile.cards.indexOf(card) === pile.cards.length - 1;
    if (isTopCard) {
      this.onStockClick?.();
    }
  }

  private handleTopCardClick(card: Card, pile: Pile): void {
    const isTopCard = pile.cards.indexOf(card) === pile.cards.length - 1;
    if (isTopCard) {
      // Prevent dragging from completed foundation piles
      if (this.rules.isFoundationComplete(pile)) {
        return;
      }
      this.startDrag(card, pile);
    }
  }

  private startDrag(card: Card, pile: Pile, startIndex?: number): void {
    if (this.dragState) return;

    const index = startIndex ?? pile.cards.indexOf(card);
    const cards = pile.type === "tableau" 
      ? pile.cards.slice(index) 
      : [card];

    // Store original positions
    const originalPositions = cards.map(c => ({
      x: c.x + pile.x,
      y: c.y + pile.y,
    }));

    // Bring drag container to front to ensure dragged cards are on top
    // But keep it below UI elements
    if (this.dragContainer.parent) {
      this.stage.removeChild(this.dragContainer);
    }
    this.stage.addChild(this.dragContainer);
    
    // Find UI manager and ensure drag container is below it
    const uiManager = this.stage.children.find(child => child.name === "UIManager");
    if (uiManager) {
      const uiIndex = this.stage.children.indexOf(uiManager);
      const dragIndex = this.stage.children.indexOf(this.dragContainer);
      // Ensure drag container is just below UI
      if (dragIndex > uiIndex) {
        this.stage.setChildIndex(this.dragContainer, uiIndex);
      }
    } else {
      // No UI found, bring drag container to front
      this.stage.setChildIndex(this.dragContainer, this.stage.children.length - 1);
    }

    // Move cards to drag container
    cards.forEach((c, i) => {
      pile.removeChild(c);
      c.x = originalPositions[i].x;
      c.y = originalPositions[i].y;
      this.dragContainer.addChild(c);
    });

    this.dragState = { cards, sourcePile: pile, originalPositions };
  }

  private handlePointerMove(e: FederatedPointerEvent): void {
    if (!this.dragState) return;

    // Convert global coordinates to stage-local coordinates
    // This accounts for stage scaling and position offset
    const stagePos = e.getLocalPosition(this.stage);
    const { cards } = this.dragState;

    cards.forEach((card, i) => {
      card.x = stagePos.x - 40; // Center on cursor
      card.y = stagePos.y - 60 + i * 30;
    });
  }

  private handlePointerUp(e?: FederatedPointerEvent): void {
    if (this.dragState) {
      const { cards, sourcePile, originalPositions } = this.dragState;
      const targetPile = this.findTargetPile(cards[0]);

      // Check if this was a quick click without much movement (potential double-click)
      // Convert to stage-local coordinates for comparison
      const wasQuickClick = cards.length > 0 && 
        this.lastClickCard === cards[0] && 
        this.lastClickPosition &&
        e && 
        (() => {
          const stagePos = e.getLocalPosition(this.stage);
          return Math.abs(stagePos.x - this.lastClickPosition!.x) < this.DOUBLE_CLICK_DISTANCE &&
                 Math.abs(stagePos.y - this.lastClickPosition!.y) < this.DOUBLE_CLICK_DISTANCE;
        })();

      if (targetPile && this.rules.canMove(cards, sourcePile, targetPile)) {
        this.completeDrag(cards, sourcePile, targetPile);
      } else {
        this.cancelDrag(cards, sourcePile, originalPositions);
      }

      // If it was a quick click, check for double-click
      if (wasQuickClick && cards.length > 0) {
        const currentTime = Date.now();
        if (currentTime - this.lastClickTime < this.DOUBLE_CLICK_DELAY) {
          // Double-click detected!
          setTimeout(() => {
            this.handleCardDoubleClick(e!, cards[0]);
          }, 50); // Small delay to ensure drag is fully cancelled
        }
      }

      this.dragState = null;
    }
  }

  private findTargetPile(card: Card): Pile | null {
    // Card position is already in stage-local coordinates
    const cardCenterX = card.x + 40;
    const cardCenterY = card.y + 60;

    for (const pile of this.board.getAllPiles()) {
      if (pile.type === "stock" || pile.type === "waste") continue;
      
      // Skip completed foundation piles as targets
      if (this.rules.isFoundationComplete(pile)) continue;

      // Pile positions are also in stage-local coordinates
      const pileX = pile.x;
      const pileY = pile.y + (pile.type === "tableau" ? pile.cards.length * 30 : 0);
      
      if (
        cardCenterX >= pileX &&
        cardCenterX <= pileX + 80 &&
        cardCenterY >= pile.y &&
        cardCenterY <= pileY + 120
      ) {
        return pile;
      }
    }
    return null;
  }

  private completeDrag(cards: Card[], sourcePile: Pile, targetPile: Pile): void {
    // Remove from source (already done visually)
    cards.forEach(card => {
      const idx = sourcePile.cards.indexOf(card);
      if (idx !== -1) sourcePile.cards.splice(idx, 1);
    });

    // Add to target
    cards.forEach(card => {
      this.dragContainer.removeChild(card);
      targetPile.addCard(card);
      this.setupCardInteraction(card);
    });

    // Reveal top card of source if it's a tableau
    if (sourcePile.type === "tableau") {
      sourcePile.revealTopCard();
    }

    this.onMoveComplete?.();
    
    // Check for completed sequences and auto-move to foundation
    this.checkAndAutoMoveCompletedSequences();
  }

  private checkAndAutoMoveCompletedSequences(): void {
    // Check all tableau piles for completed sequences
    for (const tableauPile of this.board.tableau) {
      if (this.rules.hasCompletedSequence(tableauPile)) {
        this.autoMoveCompletedSequence(tableauPile);
        // Only move one sequence at a time to avoid conflicts
        break;
      }
    }
  }

  private autoMoveCompletedSequence(tableauPile: Pile): void {
    if (tableauPile.cards.length !== 13) return;
    
    const suit = tableauPile.cards[0].suit;
    const foundationPile = this.board.getFoundationPileBySuit(suit);
    if (!foundationPile) return;
    
    // Move cards in reverse order (A first, then 2, 3, ..., K)
    // Since tableau has K down to A, we reverse the array
    const cardsToMove = [...tableauPile.cards].reverse();
    
    // Move each card to foundation
    for (const card of cardsToMove) {
      tableauPile.removeCard(card);
      foundationPile.addCard(card);
      this.setupCardInteraction(card);
    }
    
    this.onMoveComplete?.();
  }

  private cancelDrag(cards: Card[], sourcePile: Pile, originalPositions: { x: number; y: number }[]): void {
    cards.forEach((card, i) => {
      this.dragContainer.removeChild(card);
      card.x = originalPositions[i].x - sourcePile.x;
      card.y = originalPositions[i].y - sourcePile.y;
      sourcePile.addChild(card);
    });
  }
}

